import { useEffect } from "react";

const BASE_URL = "https://nearcore.io";

type SeoProps = {
  title: string;
  description: string;
  path: string;
  jsonLd?: Record<string, unknown>;
  image?: string;
};

const setMeta = (selector: string, attrs: Record<string, string>, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

export default function Seo({ title, description, path, jsonLd, image }: SeoProps) {
  const json = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    document.title = title;
    setMeta('meta[name="description"]', { name: "description" }, description);
    setMeta('meta[property="og:title"]', { property: "og:title" }, title);
    setMeta('meta[property="og:description"]', { property: "og:description" }, description);
    setMeta('meta[property="og:url"]', { property: "og:url" }, `${BASE_URL}${path}`);
    setMeta('meta[name="twitter:title"]', { name: "twitter:title" }, title);
    setMeta('meta[name="twitter:description"]', { name: "twitter:description" }, description);
    if (image) {
      setMeta('meta[property="og:image"]', { property: "og:image" }, image);
      setMeta('meta[name="twitter:image"]', { name: "twitter:image" }, image);
    }

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${BASE_URL}${path}`;

    let script = document.head.querySelector<HTMLScriptElement>("script[data-seo-jsonld]");
    if (json) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-jsonld", "true");
        document.head.appendChild(script);
      }
      script.textContent = json;
    } else if (script) {
      script.remove();
    }
  }, [title, description, path, json, image]);

  return null;
}
